import { ModalCenterRef } from '@deprecated-shared/ui/components/warehouse/modal/ModalCenter';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Cart } from '../../models/Cart';
import {
  CartAdjustModal,
  CartCreateModal,
  CartDeleteModal,
  CartEditModal,
  CartLockModal,
  CartProductsModal,
  CartUnlockModal,
} from '../modals';
import type { CartProductsModalRef } from '../modals/CartProductsModal';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithCart: (cart: Cart) => void;
}

export interface CartModalsRef {
  openCreateModal: () => void;
  openEditModal: (cart: Cart) => void;
  openDeleteModal: (cart: Cart) => void;
  openLockModal: (cart: Cart) => void;
  openUnlockModal: (cart: Cart) => void;
  openAdjustModal: (cart: Cart) => void;
  openProductsModal: (cart: Cart) => void;
}

const CartModals = forwardRef<CartModalsRef>((props, ref) => {
  const createModalRef = useRef<ModalCenterRef>(null);
  const editModalRef = useRef<ExtendedModalCenterRef>(null);
  const deleteModalRef = useRef<ExtendedModalCenterRef>(null);
  const lockModalRef = useRef<ExtendedModalCenterRef>(null);
  const unlockModalRef = useRef<ExtendedModalCenterRef>(null);
  const adjustModalRef = useRef<ExtendedModalCenterRef>(null);
  const productsModalRef = useRef<CartProductsModalRef>(null);

  // 🎯 Estado para la modal de productos (necesita props diferentes)
  // Inicializar con valores vacíos para que la modal siempre esté montada
  const [selectedCart, setSelectedCart] = useState<Cart | null>(null);

  useImperativeHandle(ref, () => ({
    openCreateModal: () => {
      createModalRef.current?.openModal();
    },
    openEditModal: (cart: Cart) => {
      editModalRef.current?.openWithCart(cart);
    },
    openDeleteModal: (cart: Cart) => {
      deleteModalRef.current?.openWithCart(cart);
    },
    openLockModal: (cart: Cart) => {
      lockModalRef.current?.openWithCart(cart);
    },
    openUnlockModal: (cart: Cart) => {
      unlockModalRef.current?.openWithCart(cart);
    },
    openAdjustModal: (cart: Cart) => {
      adjustModalRef.current?.openWithCart(cart);
    },
    openProductsModal: (cart: Cart) => {
      setSelectedCart(cart);
      // Usar setTimeout para asegurar que el estado se actualice antes de abrir
      setTimeout(() => {
        productsModalRef.current?.open();
      }, 0);
    },
  }));

  return (
    <>
      {/* 🎯 Modales - Usan patrón de estado interno para ZERO re-renders */}
      <CartCreateModal ref={createModalRef} />
      <CartEditModal ref={editModalRef} />
      <CartDeleteModal ref={deleteModalRef} />
      <CartLockModal ref={lockModalRef} />
      <CartUnlockModal ref={unlockModalRef} />
      <CartAdjustModal ref={adjustModalRef} />

      {/* 🎯 Modal de productos - Siempre renderizada */}
      <CartProductsModal
        ref={productsModalRef}
        cartCode={selectedCart?.code || ''}
        products={selectedCart?.stock_location_carts || []}
      />
    </>
  );
});

CartModals.displayName = 'CartModals';

export default CartModals;
